import { useEffect, useContext, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useAuthStore } from 'Store/Auth'
import { TableContext } from 'Context/Table'

import CustomActionCell from 'Presentation/Components/Table/Components/CustomActionCell'
import { shallow } from 'zustand/shallow'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import moment from 'moment'

import s2ab from 'Utils/s2ab'
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'

const INITIAL_FILTER = {
  page: 1,
  pageSize: 10,
  code: '',
  customerTitle: '',
  division: '',
  saleTitle: '',
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { data, totalData, getAllPagination, setSelectedId, deleteSaleOrder } = useSaleOrderStore(
    state => ({
      data: state.data,
      totalData: state.totalData,
      getAllPagination: state.getAllPagination,
      setSelectedId: state.setSelectedId,
      deleteSaleOrder: state.delete,
    }),
    shallow
  )
  const { profile } = useAuthStore(state => ({ profile: state.profile }), shallow)
  const {
    currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)

  const [filter, setFilter] = useState(INITIAL_FILTER)
  const [isTableReady, setIsTableReady] = useState(false)

  const userRoleId = profile?.roleId

  const isHideWritingAction = [2, 5, 15].includes(userRoleId)

  const handleOpenFormModal = () => {
    navigate('/sale-order/create')
  }

  const setupTable = () => {
    updateSorting('deliveryDocumentDate', true)
    updatePagination(true)
  }

  const fetchData = async (nextFilter?: typeof INITIAL_FILTER) => {
    const currentSort = currentSorting?.[0]
    const currentFilter = {
      ...(nextFilter ?? filter),
      sortBy: currentSort?.id ?? 'deliveryDocumentDate',
      order: currentSort?.desc ? 'desc' : 'asc',
    }

    try {
      updateLoading(true)
      const { success, data: responseData } = await getAllPagination(currentFilter)
      if (!success) {
        updateError(true)
      } else {
        onChangeFilter('page', responseData?.page ?? currentFilter.page)
        updateError(false)
      }
      updateLoading(false)
    } catch (err: any) {
      updateLoading(false)
      updateError(true)
    }
  }

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const submitFilter = async () => {
    const nextFilter = {
      ...filter,
      page: 1,
    }
    setFilter(nextFilter)
    await fetchData(nextFilter)
  }

  const nextPageDesktop = async (page: number) => {
    const nextFilter = {
      ...filter,
      page,
    }
    setFilter(nextFilter)
    await fetchData(nextFilter)
  }

  const clearFilter = async () => {
    setFilter(INITIAL_FILTER)
    await fetchData(INITIAL_FILTER)
  }

  const viewSaleOrder = (id: string) => {
    navigate(`/sale-order/${id}?view=1`)
    setSelectedId(id)
  }

  const editSaleOrder = (id: string) => {
    navigate(`/sale-order/${id}`)
    setSelectedId(id)
  }

  const askDeleteSaleOrder = async (id: string) => {
    const { isConfirmed } = await Swal.fire({
      title: 'คุณต้องการที่จะลบใบสั่งขายนี้ใช่หรือไม่?',
      text: 'ใบสั่งขายที่ถูกลบไปแล้วจะไม่สามารถกู้คืนได้',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (isConfirmed) {
      const { success, data } = await deleteSaleOrder(parseInt(id))
      if (success) {
        toast.success('ลบใบสั่งขายสำเร็จ')
        fetchData()
      } else {
        toast.error(data)
      }
    }
  }

  const TABLE_CONFIGS: any[] = [
    {
      Header: 'วันที่',
      accessor: 'deliveryDocumentDate',
      width: 120,
      minWidth: 120,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{moment(value).format('DD/MM/YYYY')}</span>
      },
    },
    {
      Header: 'Sale Order ID.',
      accessor: 'code',
      width: 25,
      minWidth: 25,
      Cell: ({ value, row }: any) => {
        return (
          <span className='text-gray-800'>
            {value} - {row.original.revision}
          </span>
        )
      },
    },
    {
      Header: 'ชื่อลูกค้า',
      accessor: 'customerTitle',
      width: 120,
      minWidth: 120,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'หน่วยงาน',
      accessor: 'division',
      width: 120,
      minWidth: 120,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'จำนวนเสา',
      accessor: 'totalPileAmount',
      width: 120,
      minWidth: 120,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: '',
      accessor: 'projectId',
      width: 120,
      minWidth: 120,
      Cell: ({ row }: any) => {
        return (
          row.original.projectId && (
            <Link to={`/project/${row.original.projectId}/overview`}>
              <span className='badge bg-primary'>ดูโครงการ</span>
            </Link>
          )
        )
      },
      disableSortBy: true,
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <div className='text-start'>
          <CustomActionCell
            id={row.index + 1}
            data={row.original}
            hideDuplicate
            hideEdit={isHideWritingAction}
            hideDelete={isHideWritingAction}
            onClickView={() => viewSaleOrder(row.original.id)}
            onClickEdit={() => editSaleOrder(row.original.id)}
            onClickDelete={() => askDeleteSaleOrder(row.original.id)}
          />
        </div>
      ),
      disableSortBy: true,
    },
  ]

  const exportData = () => {
    const exportedDatas = [['Sale Order ID', 'ชื่อลูกค้า', 'หน่วยงาน', 'วันที่', 'จำนวนเสา']]

    for (const item of data) {
      exportedDatas.push([
        item.code,
        item.customerTitle,
        item.division,
        moment(item.deliveryDocumentDate).isValid()
          ? moment(item.deliveryDocumentDate).format('DD/MM/YYYY')
          : '-',
        item.totalPileAmount ?? 0,
      ])
    }

    const wb = XLSX.utils.book_new()
    wb.Props = {
      Title: `Sale Order List`,
      Subject: 'List',
      Author: 'Project Management System',
      CreatedDate: new Date(),
    }
    wb.SheetNames.push('List')

    const ws = XLSX.utils.aoa_to_sheet(exportedDatas)
    ws['!cols'] = [{ width: 15 }, { width: 30 }, { width: 30 }, { width: 30 }, { width: 15 }]
    wb.Sheets['List'] = ws
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })

    saveAs(new Blob([s2ab(wbout)]), 'sale-orders.xlsx')
  }

  // Initial Table Setup
  useEffect(() => {
    setupTable()
    setIsTableReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isTableReady) {
      return
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSorting, isTableReady])

  // Reset Hook
  useEffect(() => {
    return () => {
      resetTableState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isHideWritingAction,
    data,
    totalData,
    TABLE_CONFIGS,
    handleOpenFormModal,
    exportData,
    filter,
    onChangeFilter,
    nextPageDesktop,
    submitFilter,
    clearFilter,
  }
}

export default ViewModel
